import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_PLAN_ROWS, allocationStatus, autofillRemainder, formatKronor, type PlanRow } from './milestonePlan';
import type { MilestonePlanStepState } from './useMilestonePlanStep';
import { InfoTip } from './InfoTip';
import { Notice } from '../Notice';
import { StageGuide } from './StageGuide';
import { wholeKronorInput } from '../../utils/wholeKronor';

interface PricingStepProps {
  step: MilestonePlanStepState;
  stepCounter: string;
  /** Edit mode: marker per stage (row key) against the version that was sent. */
  stageMarkers?: Record<string, { key: string; amount?: string }>;
}

interface KronorInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
  unit: string;
  placeholder?: string;
  describedBy?: string;
  inputMode?: 'numeric' | 'decimal';
  /** First field of the step: focused when the wizard opens on it. */
  autoFocusField?: boolean;
  /** Suggests the remainder: shown greyed with a dashed border while the field is empty. */
  autofilled?: boolean;
}

/**
 * Text input with a trailing unit; accepts "185 000" and keeps what was typed.
 * Kronor fields (inputMode "numeric") take whole kronor only, however the value
 * arrives (typed, pasted, autofilled) – a decimal part is removed and the field
 * says why. Percent fields (inputMode "decimal") keep decimals.
 */
function UnitInput({ id, value, onChange, invalid, unit, placeholder, describedBy, inputMode = 'numeric', autoFocusField, autofilled }: KronorInputProps) {
  const { t } = useTranslation();
  const [droppedDecimals, setDroppedDecimals] = useState(false);
  // The same field switches between kronor and percent: a notice about
  // kronor does not follow it into percent.
  useEffect(() => setDroppedDecimals(false), [inputMode]);
  const noticeId = `${id}-whole-kronor`;
  const change = (raw: string) => {
    if (inputMode === 'decimal') {
      onChange(raw.replace(/[^\d\s,.-]/g, ''));
      return;
    }
    const next = wholeKronorInput(raw, { allowNegative: true });
    setDroppedDecimals(next.droppedDecimals);
    onChange(next.value);
  };
  return (
    <>
      <div className={`input-group contract-wizard-unit-input${invalid ? ' is-invalid' : ''}${autofilled ? ' has-suggestion' : ''}`}>
        <input
          id={id}
          type="text"
          inputMode={inputMode}
          className={`form-control text-end${invalid ? ' is-invalid' : ''}${autofilled ? ' is-autofilled' : ''}`}
          value={value}
          placeholder={placeholder}
          onChange={event => change(event.target.value)}
          aria-invalid={invalid || undefined}
          aria-describedby={[describedBy, droppedDecimals ? noticeId : null].filter(Boolean).join(' ') || undefined}
          data-autofocus={autoFocusField || undefined}
        />
        <span className="input-group-text">{unit}</span>
      </div>
      {droppedDecimals && (
        <div id={noticeId} className="form-text text-danger" role="status" data-testid="whole-kronor-notice">
          {t('common.wholeKronorOnly')}
        </div>
      )}
    </>
  );
}

/** Patrick's roof example has four stages; stage 5 and later reuse the first. */
const guideStage = (number: number) => (number >= 1 && number <= 4 ? number : 1);

/** Wizard step 2 — "Pris & etappmål". Controlled by `useMilestonePlanStep`. */
export function PricingStep({ step, stepCounter, stageMarkers }: PricingStepProps) {
  const { t } = useTranslation();
  const ids = useId();
  const { state, plan, errors, showErrors } = step;
  const topDown = state.pricingMode === 'top_down';
  const percent = topDown && state.distribution === 'percent';
  const rowError = (row: PlanRow) => (showErrors ? errors.rows[row.key] ?? {} : {});
  const kr = t('contracts.wizard.pricing.unitKr');
  // The last stage's empty field suggests what is left (shown greyed). Only a
  // suggestion: it counts once the user types it or accepts it.
  const autofill = autofillRemainder(state);

  // After moving a stage, keep focus on the same move button so the user can
  // press it again; the stage itself is now at its new position.
  const moveFocusRef = useRef<{ key: string; direction: -1 | 1 } | null>(null);
  const moveStage = (key: string, direction: -1 | 1) => {
    moveFocusRef.current = { key, direction };
    step.moveRow(key, direction);
  };
  useEffect(() => {
    const target = moveFocusRef.current;
    if (!target) return;
    moveFocusRef.current = null;
    const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>(
      `[data-stage-key="${target.key}"] [data-move="${target.direction}"]`
    );
    const button = buttons?.[0];
    if (button && !button.disabled) button.focus();
    else listRef.current?.querySelector<HTMLElement>(`[data-stage-key="${target.key}"] [data-move="${-target.direction}"]`)?.focus();
  });

  // After "Lägg till etappmål", move focus to the new stage's description so it
  // scrolls into view and the user sees the result of the click.
  const listRef = useRef<HTMLOListElement>(null);
  const focusNewStageRef = useRef(false);
  const addStage = () => {
    focusNewStageRef.current = true;
    step.addRow();
  };
  useEffect(() => {
    if (!focusNewStageRef.current) return;
    focusNewStageRef.current = false;
    const textareas = listRef.current?.querySelectorAll('textarea');
    textareas?.[textareas.length - 1]?.focus();
  }, [state.rows.length]);

  // The indicator follows what the stages really cover, recomputed on every
  // change: "Allt är fördelat" only when the sum matches and no stage is empty.
  const allocation = allocationStatus(state);
  const { status } = allocation;
  const allocatedPercent = plan.afterRot > 0 ? Math.min(100, Math.max(0, (allocation.allocated / plan.afterRot) * 100)) : 0;
  const allocationState = status.kind === 'complete' ? 'is-complete' : status.kind === 'exceeded' ? 'is-over' : 'is-open';
  const percentText = (value: number) => value.toLocaleString('sv-SE', { maximumFractionDigits: 2 });
  const allocationText = (() => {
    switch (status.kind) {
      case 'pending': return t('contracts.wizard.pricing.allocationPending');
      case 'invalid': return t('contracts.wizard.pricing.allocationInvalid');
      case 'complete': return t('contracts.wizard.pricing.allocationComplete');
      case 'missingStage': return t('contracts.wizard.pricing.allocationMissingStage', { number: status.number });
      case 'remaining': return status.percent !== undefined
        ? t('contracts.wizard.pricing.allocationRemainingPercent', { percent: percentText(status.percent), amount: formatKronor(status.amount) })
        : t('contracts.wizard.pricing.allocationRemaining', { amount: formatKronor(status.amount) });
      case 'exceeded': return status.percent !== undefined
        ? t('contracts.wizard.pricing.allocationExceededPercent', { percent: percentText(status.percent), amount: formatKronor(status.amount) })
        : t('contracts.wizard.pricing.allocationExceeded', { amount: formatKronor(status.amount) });
    }
  })();

  return (
    <div className="contract-wizard-step">
      <header className="mb-4">
        <p className="contract-wizard-kicker mb-1">{stepCounter}</p>
        <h2 className="contract-wizard-step-title">{t('contracts.wizard.steps.pricing.title')}</h2>
        <p className="text-muted mb-0">{t('contracts.wizard.pricing.intro')}</p>
      </header>

      <StageGuide />

      {showErrors && errors.messages.length > 0 && (
        <div className="alert alert-danger" role="alert">
          <p className="fw-bold mb-1">{t('contracts.wizard.pricing.errorTitle')}</p>
          <ul className="mb-0 ps-3">
            {errors.messages.map(message => (
              <li key={message.key}>{t(message.key, message.params)}</li>
            ))}
          </ul>
        </div>
      )}
      {step.saveError && (
        <div className="alert alert-danger" role="alert">{step.saveError}</div>
      )}

      <section className="contract-wizard-panel mb-4" aria-labelledby={`${ids}-price`}>
        <h3 id={`${ids}-price`} className="contract-wizard-section-title mb-1">{t('contracts.wizard.pricing.priceTitle')}</h3>
        <p className="small text-muted mb-3">{t('contracts.wizard.pricing.modeQuestion')}</p>
        <div className="contract-wizard-segmented mb-3" role="group" aria-label={t('contracts.wizard.pricing.modeAria')}>
          <button type="button" aria-pressed={topDown} className={topDown ? 'is-active' : ''} onClick={() => step.setPricingMode('top_down')}>
            {t('contracts.wizard.pricing.modeTopDown')}
          </button>
          <button type="button" aria-pressed={!topDown} className={!topDown ? 'is-active' : ''} onClick={() => step.setPricingMode('bottom_up')}>
            {t('contracts.wizard.pricing.modeBottomUp')}
          </button>
        </div>

        {topDown ? (
          <>
            <div className="row g-3">
              <div className="col-12 col-md-4">
                <label htmlFor={`${ids}-total`} className="form-label fw-semibold">{t('contracts.wizard.pricing.totalLabel')}</label>
                <UnitInput id={`${ids}-total`} value={state.total} onChange={step.setTotal} unit={kr} invalid={showErrors && errors.total} autoFocusField />
              </div>
              <div className="col-12 col-md-4">
                <label htmlFor={`${ids}-after`} className="form-label fw-semibold">{t('contracts.wizard.pricing.afterRotLabel')}</label>
                <UnitInput
                  id={`${ids}-after`}
                  value={state.afterRot}
                  onChange={step.setAfterRot}
                  unit={kr}
                  placeholder={state.total}
                  invalid={showErrors && errors.afterRot}
                  describedBy={`${ids}-rot-help`}
                />
              </div>
              <div className="col-12 col-md-4">
                <span className="form-label fw-semibold d-block">{t('contracts.wizard.pricing.plannedRotLabel')}</span>
                <output className="contract-wizard-computed" aria-live="polite">{formatKronor(plan.rot)} {kr}</output>
              </div>
            </div>
            <p id={`${ids}-rot-help`} className="form-text mb-0 mt-2">
              <span className="d-block">{t('projectView.wizard.noRotHint')}</span>
              <span className="d-block">{t('contracts.wizard.pricing.rotResponsibility')}</span>
            </p>
          </>
        ) : (
          <div className="contract-wizard-note" role="note">
            <i className="feather-info mt-1" aria-hidden="true"></i>
            <span>{t('contracts.wizard.pricing.bottomUpNote')}</span>
          </div>
        )}
      </section>

      {/* Sticks to the top of the wizard while the stages scroll, so the status is always visible. */}
      <section className="contract-wizard-panel contract-wizard-allocation-panel mb-3" aria-labelledby={`${ids}-alloc`}>
        <h3 id={`${ids}-alloc`} className="visually-hidden">{t('contracts.wizard.pricing.allocationTitle')}</h3>
        {topDown ? (
          <>
            <dl className="contract-wizard-allocation">
              <div>
                <dt>{t('contracts.wizard.pricing.toAllocate')}</dt>
                <dd>{formatKronor(plan.afterRot)} {kr}</dd>
              </div>
              <div>
                <dt>{t('contracts.wizard.pricing.allocated')}</dt>
                <dd>{formatKronor(allocation.allocated)} {kr}</dd>
              </div>
              <div>
                <dt>{t('contracts.wizard.pricing.status')}</dt>
                <dd className={`contract-wizard-allocation-status ${allocationState}`} aria-live="polite">{allocationText}</dd>
              </div>
            </dl>
            <div
              className="progress contract-wizard-progress"
              role="progressbar"
              aria-label={t('contracts.wizard.pricing.allocationTitle')}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(allocatedPercent)}
            >
              {/* Runtime width (docs/styling.md §6). */}
              <div className={`progress-bar ${allocationState}`} style={{ width: `${allocatedPercent}%` }}></div>
            </div>
          </>
        ) : (
          <dl className="contract-wizard-allocation">
            <div>
              <dt>{t('contracts.wizard.pricing.totalLabel')}</dt>
              <dd>{formatKronor(plan.gross)} {kr}</dd>
            </div>
            <div>
              <dt>{t('contracts.wizard.pricing.plannedRotLabel')}</dt>
              <dd>{formatKronor(plan.rot)} {kr}</dd>
            </div>
            <div>
              <dt>{t('contracts.wizard.pricing.afterRotLabel')}</dt>
              <dd>{formatKronor(plan.afterRot)} {kr}</dd>
            </div>
          </dl>
        )}
      </section>

      {topDown && (
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <div className="contract-wizard-segmented" role="group" aria-label={t('contracts.wizard.pricing.distributionAria')}>
            <button type="button" aria-pressed={!percent} className={!percent ? 'is-active' : ''} onClick={() => step.setDistribution('amount')}>
              {t('milestones.wizard.modeAmount')}
            </button>
            <button type="button" aria-pressed={percent} className={percent ? 'is-active' : ''} onClick={() => step.setDistribution('percent')}>
              {t('contracts.wizard.pricing.modePercentShort')}
            </button>
          </div>
          <button type="button" className="btn btn-link contract-wizard-link" onClick={step.distributeEvenly}>
            {t('contracts.wizard.pricing.splitEvenly')}
          </button>
        </div>
      )}

      {step.amountsCleared && (
        <Notice
          level="info"
          className="mb-3"
          testId="stage-amounts-cleared"
          title={t('contracts.wizard.pricing.amountsCleared.title')}
        >
          {t(step.amountsCleared === 'bottom_up'
            ? 'contracts.wizard.pricing.amountsCleared.bottomUp'
            : 'contracts.wizard.pricing.amountsCleared.topDown')}
        </Notice>
      )}

      <ol ref={listRef} className="list-unstyled d-flex flex-column gap-3 mb-3">
        {state.rows.map((row, index) => {
          const rowErr = rowError(row);
          const number = index + 1;
          const marker = stageMarkers?.[row.key];
          const autofilled = autofill !== null && index === state.rows.length - 1;
          return (
            <li key={row.key} data-stage-key={row.key} className={`contract-wizard-stage${topDown ? '' : ' is-bottom-up'}`}>
              <div className="contract-wizard-stage-order">
                <span className="contract-wizard-stage-number" aria-hidden="true">{number}</span>
                {state.rows.length > 1 && (
                  <div className="contract-wizard-stage-move" role="group" aria-label={t('contracts.wizard.pricing.moveStageGroup', { number })}>
                    <button
                      type="button"
                      className="btn btn-link contract-wizard-icon-btn"
                      data-move="-1"
                      onClick={() => moveStage(row.key, -1)}
                      disabled={index === 0}
                      aria-label={t('contracts.wizard.pricing.moveStageUp', { number })}
                    >
                      <i className="feather-chevron-up" aria-hidden="true"></i>
                    </button>
                    <button
                      type="button"
                      className="btn btn-link contract-wizard-icon-btn"
                      data-move="1"
                      onClick={() => moveStage(row.key, 1)}
                      disabled={index === state.rows.length - 1}
                      aria-label={t('contracts.wizard.pricing.moveStageDown', { number })}
                    >
                      <i className="feather-chevron-down" aria-hidden="true"></i>
                    </button>
                  </div>
                )}
              </div>
              <div className="contract-wizard-stage-description">
                <div className="d-flex flex-wrap align-items-center gap-2 mb-2 position-relative">
                  <label htmlFor={`${ids}-${row.key}-desc`} className="form-label small fw-semibold mb-0">
                    {t('contracts.wizard.pricing.stageDescriptionLabel', { number })}
                  </label>
                  <InfoTip label={t('contracts.wizard.pricing.guide.infoAria.description')} fit>
                    <strong>{t('contracts.wizard.pricing.guide.descriptionTip.label')}</strong>{' '}
                    {t('contracts.wizard.pricing.guide.descriptionTip.text')}
                    <hr />
                    {t('contracts.wizard.pricing.guide.exampleLabel')}{' '}
                    <em>
                      {t(`contracts.wizard.pricing.guide.example.stages.${guideStage(number)}.title`)}
                      {' – '}
                      {t(`contracts.wizard.pricing.guide.example.stages.${guideStage(number)}.description`)}
                    </em>
                    <hr />
                    {t('contracts.wizard.pricing.guide.exampleLabel')}{' '}
                    <em>{t('contracts.wizard.pricing.guide.descriptionTipExample2')}</em>
                  </InfoTip>
                  {marker && (
                    <span className="wizard-changed-badge">
                      {t(`contracts.wizard.edit.stageMarker.${marker.key}`, { amount: marker.amount })}
                    </span>
                  )}
                </div>
                <textarea
                  id={`${ids}-${row.key}-desc`}
                  className={`form-control${rowErr.description ? ' is-invalid' : ''}`}
                  rows={2}
                  value={row.description}
                  placeholder={t(`contracts.wizard.pricing.guide.placeholders.stage${guideStage(number)}`)}
                  onChange={event => step.setRow(row.key, { description: event.target.value })}
                  aria-invalid={rowErr.description || undefined}
                  aria-describedby={`${ids}-${row.key}-desc-help`}
                  data-autofocus={(!topDown && index === 0) || undefined}
                />
                <p id={`${ids}-${row.key}-desc-help`} className="form-text mb-0">
                  {t('contracts.wizard.pricing.guide.descriptionHelp')}
                </p>
              </div>
              <div className="contract-wizard-stage-amount">
                <div className="d-flex align-items-center gap-2 mb-2 position-relative">
                  <label htmlFor={`${ids}-${row.key}-amount`} className="form-label small fw-semibold mb-0">
                    {topDown
                      ? percent ? t('contracts.wizard.pricing.shareLabel') : t('contracts.wizard.pricing.amountLabel')
                      : t('contracts.wizard.pricing.grossLabel')}
                  </label>
                  <InfoTip label={t('contracts.wizard.pricing.guide.infoAria.amount')} alignRight fit>
                    <strong>{t('contracts.wizard.pricing.guide.amountTip.label')}</strong>{' '}
                    {t('contracts.wizard.pricing.guide.amountTip.text')}
                  </InfoTip>
                </div>
                <UnitInput
                  id={`${ids}-${row.key}-amount`}
                  value={percent ? row.percent : row.amount}
                  onChange={value => step.setRow(row.key, percent ? { percent: value } : { amount: value })}
                  unit={percent ? '%' : kr}
                  inputMode={percent ? 'decimal' : 'numeric'}
                  invalid={rowErr.amount}
                  placeholder={autofilled && autofill !== null ? autofill : undefined}
                  autofilled={autofilled}
                  describedBy={autofilled ? `${ids}-${row.key}-autofill` : undefined}
                />
                {autofilled && autofill !== null && (
                  <>
                    <p id={`${ids}-${row.key}-autofill`} className="text-explain text-end mt-1 mb-1" data-testid="stage-autofill-note">
                      {t('contracts.wizard.pricing.suggestion.note')}
                    </p>
                    <div className="d-flex justify-content-end">
                      <button
                        type="button"
                        className="btn btn-outline-secondary contract-wizard-autofill-accept"
                        onClick={step.acceptSuggestion}
                        data-testid="stage-autofill-accept"
                      >
                        {percent
                          ? t('contracts.wizard.pricing.suggestion.acceptPercent', { percent: autofill })
                          : t('contracts.wizard.pricing.suggestion.acceptAmount', { amount: autofill })}
                      </button>
                    </div>
                  </>
                )}
                {/* A suggestion is not counted, so its stage has no share or amount to show. */}
                {topDown && !autofilled && (
                  <span className="form-text d-block text-end">
                    {percent
                      ? t('contracts.wizard.pricing.equalsAmount', { amount: formatKronor(plan.stageAfterRot[index]) })
                      : t('contracts.wizard.pricing.shareOfTotal', {
                          percent: plan.afterRot > 0 && Number.isFinite(plan.stageAfterRot[index])
                            ? (Math.round((plan.stageAfterRot[index] / plan.afterRot) * 1000) / 10).toLocaleString('sv-SE')
                            : '0',
                        })}
                  </span>
                )}
              </div>
              {!topDown && (
                <div className="contract-wizard-stage-amount">
                  <label htmlFor={`${ids}-${row.key}-rot`} className="form-label small fw-semibold">{t('contracts.wizard.pricing.stageRotLabel')}</label>
                  <UnitInput
                    id={`${ids}-${row.key}-rot`}
                    value={row.rotAmount}
                    onChange={value => step.setRow(row.key, { rotAmount: value })}
                    unit={kr}
                    invalid={rowErr.rotAmount}
                  />
                </div>
              )}
              <div className="contract-wizard-stage-date">
                <label htmlFor={`${ids}-${row.key}-date`} className="form-label small fw-semibold">
                  {t('contracts.wizard.pricing.endDateLabel')}{' '}
                  <span className="fw-normal text-muted">{t('contracts.descriptions.otherOptional')}</span>
                </label>
                <input
                  id={`${ids}-${row.key}-date`}
                  type="date"
                  className="form-control"
                  value={row.endDate}
                  onChange={event => step.setRow(row.key, { endDate: event.target.value })}
                />
              </div>
              <button
                type="button"
                className="btn btn-link contract-wizard-icon-btn contract-wizard-stage-remove"
                onClick={() => step.removeRow(row.key)}
                disabled={state.rows.length <= 1}
                aria-label={t('contracts.wizard.pricing.removeStageAria', { number })}
              >
                <i className="feather-trash-2" aria-hidden="true"></i>
              </button>
            </li>
          );
        })}
      </ol>

      <div className="d-flex align-items-center gap-2 position-relative">
        <button
          type="button"
          className="btn contract-wizard-add-stage flex-grow-1"
          onClick={addStage}
          disabled={state.rows.length >= MAX_PLAN_ROWS}
        >
          <i className="feather-plus me-2" aria-hidden="true"></i>
          {t('contracts.wizard.pricing.addStage')}
        </button>
        <InfoTip label={t('contracts.wizard.pricing.guide.infoAria.count')} alignRight>
          <strong>{t('contracts.wizard.pricing.guide.countTip.label')}</strong>{' '}
          {t('contracts.wizard.pricing.guide.countTip.text')}
        </InfoTip>
      </div>
      <p className="form-text mt-2 mb-0">{t('milestones.form.endDateHelp')}</p>
    </div>
  );
}

export default PricingStep;
